import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { featuredComics } from "@/mocks/comics";
import type { Comic } from "@/mocks/comics";

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const total = featuredComics.length;

  const goTo = useCallback(
    (index: number) => {
      setCurrent(((index % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    const timer = setInterval(() => goTo(current + 1), 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const comic: Comic = featuredComics[current];

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={comic.cover}
          alt={comic.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-background-50"></div>
      </div>

      <div className="relative z-10 w-full h-full flex items-end">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pb-12 md:pb-16">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="hidden md:block w-40 h-56 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 pt-2">
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-label ${
                    comic.status === "连载"
                      ? "bg-accent-500 text-background-50"
                      : "bg-foreground-600 text-background-50"
                  }`}
                >
                  {comic.status}
                </span>
                <span className="text-sm text-background-50/80 font-label">
                  {comic.author} · 共{comic.chapters}话
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-3">
                {comic.title}
              </h2>
              <p className="text-sm md:text-base text-background-50/70 max-w-xl line-clamp-2 mb-5">
                {comic.description}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/comic/${comic.id}`}
                  className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读
                </Link>
                <span className="text-sm text-background-50/60 font-label">
                  <i className="ri-eye-line mr-1"></i>
                  {(comic.views / 10000).toFixed(0)}万 阅读
                </span>
                <span className="text-sm text-background-50/60 font-label">
                  <i className="ri-star-fill text-accent-400 mr-1"></i>
                  {comic.rating}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => goTo(current - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur-sm transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl text-background-50"></i>
      </button>
      <button
        onClick={() => goTo(current + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur-sm transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl text-background-50"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {featuredComics.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all rounded-full cursor-pointer ${
              i === current
                ? "w-6 h-2 bg-primary-500"
                : "w-2 h-2 bg-background-50/50 hover:bg-background-50/80"
            }`}
            aria-label={`第${i + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}